import React from 'react'
import Columns from '@toasttab/shared/src/components/organisms/MDS/Columns/Columns'
import { Asset } from '@toasttab/shared/src/interfaces/ContentfulInterface.d'
import {
  ColumnsChildrenProps,
  ColumnsProps
} from '@toasttab/shared/src/components/organisms/MDS/Columns/Columns.d'
import { ContentfulImageAPI } from '@toasttab/shared/src/utils/contentfulUtils'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import { buildCtas } from '@toasttab/shared/src/utils/Contentful/ctas'

interface ContentfulMdsCTABannerProps {
  ctas: any[]
  dataTestId?: string
  description?: any
  heading?: string
  eyebrow?: string
  media?: Asset
  theme?: string
  uniqueId?: string
}

const ContentfulMdsCTABanner = ({
  ctas,
  dataTestId = 'ContentfulMdsCTABanner-1',
  description,
  eyebrow,
  heading,
  media,
  theme,
  uniqueId
}: ContentfulMdsCTABannerProps) => {
  const { prefix } = useI18next()
  const leftColumn: any = []
  const rightColumn: any = []
  // Left columm
  if (media) {
    let query = ''
    let imageUrl = media.file.url
    if (!imageUrl.includes('http')) {
      imageUrl = `https:${imageUrl}`
    }
    let srcset = imageUrl
    if (media.file.contentType === 'image/png') {
      query = ContentfulImageAPI.transformImageAvif()
      srcset = ContentfulImageAPI.transformImageAvifSrcset({
        url: imageUrl,
        widths: [320, 640, 768, 1024, 1280, 1536, 1920]
      })
    }

    leftColumn.push({
      imageResolutions: [
        {
          borderRadius: {
            label: 'Medium'
          },
          desktop: [
            {
              extension: media.file.contentType,
              imageAlt: '',
              imageColor: '',
              url: imageUrl,
              title: media?.title,
              lazyLoad: true,
              optimizedImages: {
                src: '',
                srcset: imageUrl,
                srcsetMaxWidth: '',
                srcsetAvif: srcset,
                placeholderBox: '',
                placeholderImage: '',
                maxSrcsetWidth: '',
                srcAvif: imageUrl + query
              }
            }
          ],
          title: '',
          typeHandle: 'images'
        }
      ],
      toggleSwitch: false,
      typeHandle: 'image'
    })
  }

  // Right column
  if (eyebrow) {
    rightColumn.push({
      typeHandle: 'eyebrow',
      componentClass: '!mb-8 mt-2 md:mt-[-24px] text-secondary',
      content: eyebrow
    })
  }

  if (heading) {
    rightColumn.push({
      as: leftColumn.length ? 'h3' : 'h2',
      classes: eyebrow ? 'mb-8' : 'mb-3',
      sectionHeading: heading,
      typeHandle: 'heading',
      variation: leftColumn.length ? 'h3' : 'h2'
    })
  }

  if (description) {
    rightColumn.push({
      classes: eyebrow ? 'mb-8 !text-gray-100' : 'mb-6 !text-gray-100',
      typeHandle: 'description',
      wysiwygV2: description.childMarkdownRemark.html
    })
  }

  if (ctas) {
    const ctasProps = buildCtas(ctas)
    rightColumn.push(...ctasProps)
  }

  const columnsProps: any = []
  if (leftColumn && leftColumn.length > 0) {
    columnsProps.push({
      children: leftColumn
    })
  }
  if (rightColumn && rightColumn.length > 0) {
    columnsProps.push({
      children: rightColumn
    })
  }

  const ctaColumnsChildren: ColumnsChildrenProps[] = [
    {
      columnContents: columnsProps,
      typeHandle:
        columnsProps?.length === 1 ? 'oneColumnContent' : 'twoColumnContent'
    }
  ]

  // Set background color based on theme
  // If no theme is set (this was added late), default to old behavior, which is gray background if media is present
  let bgColor: 'bg-white' | 'bg-gray-0' = 'bg-white'
  if (theme) {
    bgColor = theme === 'White' ? 'bg-white' : 'bg-gray-0'
  } else if (media) {
    bgColor = 'bg-gray-0'
  }

  const ctaColumns: ColumnsProps = {
    bgColor,
    textAlignment: columnsProps?.length === 1 ? 'center' : 'left',
    gridClasses: '!gap-6',
    sectionDescription: '',
    sectionHeading: '',
    toggleSwitch: true, // backgroundColor Full Bleed
    icon: [],
    children: ctaColumnsChildren,
    uniqueId,
    dataTestId
  }
  return <Columns {...(ctaColumns as any)} />
}

export default ContentfulMdsCTABanner
